// Wire contracts for the OPG CoPilot backend skill (CR-20260716-001, contracts C-1/C-2).
//
// These mirror the FROZEN backend DTOs (ets.platform: ETS.ShellService.Shell.Protocol
// /Operations|Dto/OpgCoPilot). Field names are camelCase — the shell-router COMMAND path
// serializes with plain JSON.stringify (NO class-transformer) and the backend Newtonsoft
// deserializes case-insensitively into the PascalCase C# DTOs. The inbound ESS push arrives
// camelCase on the FE (mirrors the Hedge CoPilotSolutionsReadyDto). Do NOT add
// class-transformer decorators.
//
// Send: RunOpgCoPilotSkill (command). NO Cancel op in v1 (ADR-033: supersede + the FE teardown
// timer + the 40s backend run budget replace it). Receive: OpgCoPilotTradesReadyDto over ESS,
// correlated by runId.
//
// DATE RULE (C-1): every date crosses as a calendar STRING 'yyyy-MM-dd' (a chain key), NEVER a
// JS Date — a Date serialized at local midnight shifts a calendar day when parsed as an instant
// in another timezone (the Hedge donor maps FE-side the same way; backend WIRE-01 proves the
// string is timezone-inert). The mapping lives in backend-opg-trades-skill.ts.

import {ProtocolCommand} from '../service-model/protocol-command.interface';
import {SHELL_PROTOCOL_OPERATIONS_NS} from '../shell-communication/operations/shell-protocol-namespace.const';

// Wire version of the RunOpgCoPilotSkill contract (backend RunOpgCoPilotSkill.ContractVersion).
// Bump only in lockstep with a backend contract change.
// 4 since CR-20260727-002 (3 since CR-20260727-001, 2 since CR-20260722-002, 1 originally):
//   v2  marketOverrides    omitted = "measure everything"       = v1 behaviour   (additive)
//   v3  expectedRangeModel omitted = "ATM Straddle" (scale 1)   = v2 behaviour   (additive)
//   v4  priceRange.direction REMOVED                                             (NOT additive)
//
// v4 is this family's FIRST non-additive bump, so the "every bump has been purely additive" claim
// that stood here is no longer true and must not be restored.
//
// It is still safe in both directions, and only because the field was already dead:
//   v3 client -> v4 backend: the CQRS boundary deserializes with default Newtonsoft settings,
//     which IGNORE unknown members — a stray `direction` is dropped, exactly as before.
//   v4 client -> v3 backend: Direction deserializes to null and was never read
//     (OpgRangeReducer.cs:105-107 derives the direction from the SIDE).
//
// NOTE this number is documentary. Nothing in backend production code compares ContractVersion —
// only tests set it. Do not read the bump as a guard that exists.
export const OPG_COPILOT_CONTRACT_VERSION = 6;

// The ESS message type the FE subscribes to (= backend class name; MessageGuid
// b205178a-caa4-4eb2-8ec4-11fe890c9488 server-side).
export const OPG_COPILOT_TRADES_READY_MESSAGE_TYPE = 'OpgCoPilotTradesReadyDto';

// The closed set of backend-emittable run outcomes (C-2, backend OpgCoPilotRunOutcomes).
// The client resolves succeeded/empty and rejects superseded/terminalFailed/preSendBlocked.
export const OPG_COPILOT_OUTCOME_SUCCEEDED = 'succeeded';
export const OPG_COPILOT_OUTCOME_EMPTY = 'empty';
export const OPG_COPILOT_OUTCOME_SUPERSEDED = 'superseded';
export const OPG_COPILOT_OUTCOME_TERMINAL_FAILED = 'terminalFailed';
export const OPG_COPILOT_OUTCOME_PRE_SEND_BLOCKED = 'preSendBlocked';

// ---- Request-side DTOs (FE -> BE, on RunOpgCoPilotSkill) -------------------------------
// Discriminated unions are encoded as a kind/mode string + the variant fields (null when not
// applicable), mirroring the backend OpgCoPilotInputs.cs field-for-field.

// Mirrors OpgPriceRange. kind = "strikes" | "followThrough" | "atmOffset" | "expectedRange".
// CR-20260727-002 removed `direction` ("up" | "down", followThrough only). It was never read: the
// backend derives the coverage direction from the SIDE (OpgRunNormalizer passes CallsUp / PutsDown
// into OpgRangeReducer). Carrying it meant a puts side could send "up" and nothing anywhere
// objected. Do NOT re-add it — a field that only ever expresses a contradiction is worse than
// absent, and the receiving property is gone.
export interface OpgCoPilotPriceRangeDto {
    kind: string;
    startStrike: number | null;               // strikes + followThrough + expectedRange
    endStrike: number | null;                 // strikes only
    anchor: string | null;                    // atmOffset only: "chosen" | "market"
    offset: number | null;                    // atmOffset only: SIGNED dollar offset off the anchor ATM
    sub: OpgCoPilotAtmOffsetSubDto | null;    // atmOffset only
}

// Mirrors OpgAtmOffsetSub. kind = "range" | "followThrough" | "both"; value present for
// range/both (it sizes the range family), null for followThrough.
export interface OpgCoPilotAtmOffsetSubDto {
    kind: string;
    value: OpgCoPilotAtmRangeValueDto | null;
}

// Mirrors OpgAtmRangeValue. mode = "dollar" (extent = static positive extent) |
// "expectedRange" (dynamic — the skill computes the per-date end itself; carries NO number).
export interface OpgCoPilotAtmRangeValueDto {
    mode: string;
    extent: number | null;
}

// Mirrors OpgMaxRisk. kind = "dollar" | "relative". NULLABLE where it sits on the side
// inputs: null = the risk question DOES NOT EXIST for that side (a follow-through-only
// coverage — hide-risk-for-follow-through, pkg-03) — never "unanswered".
// ⚠ `normalized` is GONE (contract v5, CR-20260827-002): the amount crosses in PLAIN DOLLARS
// only — the adapter converts a normalized entry (× defaultQty × 100) before sending.
export interface OpgCoPilotMaxRiskDto {
    kind: string;
    amount: number | null;    // dollar: PLAIN DOLLARS, always; null = the normalized entry had
                              // no usable Default Qty to convert with (backend refuses loudly)
    level: string | null;     // relative: "Low" | "Moderate" | "Aggressive" | "All"
}

export interface OpgCoPilotSideInputsDto {
    mode?: 'custom' | 'preset';
    presetId?: string;
    presetAnchor?: number;
    presetApgSource?: string;
    presetQuantity?: number;
    priceRange: OpgCoPilotPriceRangeDto;
    // ⚠ RENAMED from the `minProfit {amount, normalized}` object (contract v5,
    // CR-20260827-002), mirroring the Adjust wire's MinProfitDollars: PLAIN DOLLARS, always.
    // 0 = the refusal sentinel (unconvertible normalized entry) — the backend rejects a
    // non-positive W by name rather than running on a number nobody typed.
    minProfitDollars: number;
    maxRisk: OpgCoPilotMaxRiskDto | null;
}

// Mirrors OpgCoPilotContext (= the seam OpgSkillContext + the ADR-037 `underlying` amendment).
// panelInstanceId deliberately does NOT ride here — it is a TOP-LEVEL field of the op (C-1);
// the adapter hoists it out of the seam-level context.
export interface OpgCoPilotContextDto {
    underlying: string;                    // EMPTY fails backend pre-send validation -> preSendBlocked
    expirations: string[];                 // VISIBLE-filtered 'yyyy-MM-dd' chain keys
    selectedExpiration: string | null;
    strikeLadder: number[];
    chosenAtm: number;
    dynamicAtm: number;
    // ⚠ `defaultQty` is GONE (contract v5, CR-20260827-002): the engine computes per-contract
    // and the adapter consumes the qty as its normalized->dollars conversion input instead.
}

// CR-20260924-002 (ES mode). Mirrors the backend EsContractCandidateDto: one ES contract the lens
// may translate onto, nearest first; gap = the Hedge Matrix silo's fair-value offset when the silo
// exists (the lens then uses the silo's own number), null = the backend reads the day's fair value.
export interface OpgEsContractCandidateDto {
    contract: string;
    gap: number | null;
}

// Mirrors the backend EsContractGapDto (on the push): the gap the lens used per contract.
export interface OpgEsContractGapDto {
    contract: string;
    gap: number;
}

// User-supplied market conditions for ONE date (CR-20260722-002), mirroring the backend
// OpgCoPilotMarketOverride field-for-field. Sent ONLY for dates the user actually edited — a
// seeded-but-untouched date must not appear here, or every date would silently override.
// Nullable throughout so ABSENT stays distinguishable from a legitimate ZERO (a vertical skew of
// 0 classifies as Flat and is a valid reading); the backend validator rejects an incomplete record.
// CR-20260921-002: every value is PER SIDE (client ruling 2026-09-21, replacing D5's single IV/HS).
// A side's IV and vertical skew are required iff that side runs; HS is carried, never consumed.
export interface OpgCoPilotMarketOverrideDto {
    date: string;             // 'yyyy-MM-dd' chain key
    ivCall: number | null;    // FRACTION (0.138 = 13.8%), matching ImpliedVolatilitySnapshot.Value
    ivPut: number | null;
    vsCall: number | null;    // raw vertical-skew index, pre-classification
    vsPut: number | null;
    hsCall: number | null;    // carried; no backend consumer reads horizontal skew (ruling D4)
    hsPut: number | null;
}

// The user's App Settings -> Expected Move Settings MODEL (CR-20260727-001), mirroring the backend
// OpgCoPilotExpectedRangeModel field-for-field. Sent so the 'expectedRange' price-range forms
// resolve under the model the panel is actually showing rather than always the ATM straddle.
//
// COURIER, NOT A VALUE. This carries the model SELECTION only — never a boundary, band or
// magnitude. Resolving the expected range frontend-side is prohibited (owner ruling
// CR-20260715-001; ADR-035 lists "FE-computed boundary on the wire" among its rejected paths), and
// the skill still does the math from its own market facts.
//
// Null/absent = "ATM Straddle" = byte-for-byte contract-v2 behaviour.
export interface OpgCoPilotExpectedRangeModelDto {
    model: string;                  // "ATM Straddle" | "Custom Percentage" — the literals ARE the contract
    customPercent: number | null;   // Custom Percentage only: a MULTIPLIER of the straddle (0.01-5.0), NOT a percent of spot
}

// ---- Command ---------------------------------------------------------------------------

// Run one questionnaire submission. ASYNC-fire: the call returns an ack immediately while the
// run executes off-thread (<=40s backend budget, ADR-033); the result arrives as
// OpgCoPilotTradesReadyDto over ESS, correlated by runId. TerminalId is resolved SERVER-side
// (never sent). Exactly one date variant per dateKind; exactly the tab's side(s) non-null.
export class RunOpgCoPilotSkill implements ProtocolCommand {
    constructor(
        public readonly contractVersion: number,
        public readonly runId: string,
        public readonly panelInstanceId: string,
        public readonly tab: string,
        public readonly dateKind: string,
        public readonly date: string | null,
        public readonly rangeStart: string | null,
        public readonly rangeEnd: string | null,
        public readonly calls: OpgCoPilotSideInputsDto | null,
        public readonly puts: OpgCoPilotSideInputsDto | null,
        public readonly context: OpgCoPilotContextDto,
        // APPENDED, never inserted: buildRunCommand passes these positionally, so a new arg placed
        // mid-list would silently misalign every following one.
        public readonly marketOverrides: OpgCoPilotMarketOverrideDto[] | null = null,
        // CR-20260727-001, appended for the same reason.
        public readonly expectedRangeModel: OpgCoPilotExpectedRangeModelDto | null = null,
        // CR-20260924-002 (ES mode, Hedge Matrix host only), appended for the same reason. The
        // run stays in SPX terms; the backend handler passes the RESULT through the ES lens.
        // Absent/false = today's behaviour byte for byte (the OPG panel never sets it).
        public readonly esMode: boolean = false,
        public readonly esContracts: OpgEsContractCandidateDto[] | null = null
    ) {}

    getOperationName(): string {
        return SHELL_PROTOCOL_OPERATIONS_NS + '.RunOpgCoPilotSkill';
    }
}

// ---- Receive-side DTOs (BE -> FE, ESS push; camelCase as observed on the FE) ------------

// One leg descriptor — the DV-2 freeze of PricingGridLegDescriptor, field-for-field.
// Optional FE members arrive as JSON null from the backend (nullable C# members).
export interface OpgCoPilotLegDto {
    side: string | null;               // "Buy" | "Sell"
    qty: number | null;
    width: number | null;
    expirationOffset: number | null;   // BUSINESS DAYS from the trade's OWN expiration
    type: string;                      // "leg" | "width"
}

// One skill-authored trade — mirrors the frontend OpgTradeContent field-for-field (C-2).
// v1 emits NO score (the FE field stays optional; cards already hide the badge when absent).
// mold is a StrategyTemplate literal (frozen DV-2 enumeration; the v1 engine emits only
// "Vertical" and "Condor").
export interface OpgCoPilotTradeDto {
    presetId?: string;
    presetRevision?: number;
    presetName?: string;
    presetProvenance?: string;
    name: string;                      // "Put Condor" — the underlying is DROPPED unless it is a FUTURES contract ("ESH6 Put Condor"), where the month is part of what the trade is. NO "Unbalanced", NO risk suffix: those are `moldDescription`'s, Summary-only by owner ruling
    side: string;                      // "calls" | "puts"
    expiration: string;                // 'yyyy-MM-dd' chain key — one of the request context's expirations
    startingStrike: number;
    mold: string;
    legs: OpgCoPilotLegDto[] | null;
    width: number;
    priceAtSelection: number | null;   // signed credit(+)/debit(-); pre-first-tick fallback only; null = unpriced (card shows the em dash)
    maxRiskLabel: string | null;       // zone label | "$X" | "" on a follow-through in a Both run
    tradeType: string | null;          // "range" | "followThrough"; REQUIRED in a mixed (Both) result, null in single-family runs
    score: number | null;              // display-only fake score (M2 smoke ruling): 99..50, non-increasing along the ranked list

    // CR-20260917-001. The structure named for a human, delivered alongside the short `name`
    // above — which is UNCHANGED, because the ORION (OPG) panel renders it and the owner ruled
    // this naming Maven-only (2026-09-17, answer 5).
    //
    // ⚠ `moldDescription` is PROSE and is NOT a StrategyTemplate member — it is never
    // interchangeable with `mold`. Read `mold` to build a strategy; read this to show a person
    // what the hedge is.
    // OPTIONAL on purpose: protobuf omits a default, so a shell that predates this CR sends none
    // of the three and the fields arrive undefined. Absent `unbalanced` reads as balanced.
    unbalanced?: boolean;              // the brake pair trades at a different quantity than the accelerator pair; DELIVERED, never re-derived from `legs` (a balanced butterfly IS 1/-2/1)
    moldDescription?: string | null;   // "[Unbalanced ]<Mold> - <risk>", e.g. "Unbalanced Condor - Aggressive", "Condor - Low", "Condor - $500 risk" — the MAVEN SUMMARY's string, and ONLY the Summary's
    // CR-20260924-002 (ES mode): the ES contract the backend lens translated this trade onto
    // ('ESZ6'); startingStrike is then the ES strike and every leg qty the ES count. Absent/null
    // on every non-ES-mode run.
    esContract?: string | null;
}

// The run-outcome push. Topic = owning TerminalId (ESS routing); runId echoes the command.
// Exactly one terminal push per run — succeeded/empty/terminalFailed/preSendBlocked for the
// current run, 'superseded' to a DISPLACED runId (ADR-033).
export interface OpgCoPilotTradesReadyDto {
    topic: string;
    runId: string;
    outcome: string;                   // OPG_COPILOT_OUTCOME_*
    failureDetail: string | null;
    calls: OpgCoPilotTradeDto[] | null;   // per-side trades; empty/null on every non-succeeded outcome
    puts: OpgCoPilotTradeDto[] | null;
    // CR-20260902-001 (additive on the push): why a side is EMPTY, in the trader's words — one
    // line per affected date, '\n'-joined. Null/absent = nothing beyond the generic empty message.
    // Producers today: the expected-range boundary at/short of the start strike; the unreachable
    // expected-range edge.
    callsNote?: string | null;
    putsNote?: string | null;
    // CR-20260924-002 (additive on the push): on an ES-mode run, the gap the lens used per
    // contract — the number a missing silo is created on. Absent/null on every other run.
    esContracts?: OpgEsContractGapDto[] | null;
}
